import Extent from '@arcgis/core/geometry/Extent';
import MediaLayer from '@arcgis/core/layers/MediaLayer.js';
import ExtentAndRotationGeoreference from '@arcgis/core/layers/support/ExtentAndRotationGeoreference.js';
import ImageElement from '@arcgis/core/layers/support/ImageElement.js';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import MapView from '@arcgis/core/views/MapView';
import { DEFAULT_MAP_SOURCE } from '@env';
import { Observable, Subject } from 'rxjs';

import { SnLonLatBounds, SnLonLatBoundsLike } from '../coordinates';
import { GoogleMapImageOverlay } from './helpers/google-image';

export class FsImageLayer {
  public overlay: MediaLayer | google.maps.OverlayView;

  public labelClass: LabelClass;
  public labelMarker: google.maps.marker.AdvancedMarkerElement;

  private readonly _url: string;
  private readonly _rotation: number;
  private readonly _lonLatBounds: SnLonLatBounds;
  private readonly _clickable: boolean;
  private readonly _id: string;

  private readonly _newClick$: Subject<any> = new Subject();
  private readonly _newHover$: Subject<any> = new Subject();

  get newClick$(): Observable<any> {
    return this._newClick$.asObservable();
  }

  get newHover$(): Observable<any> {
    return this._newHover$.asObservable();
  }

  get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  constructor(data: {
    url: string;
    rotation: number;
    id: string;
    lonLatBounds?: SnLonLatBoundsLike;
    clickable?: boolean;
  }) {
    this._url = data.url;
    this._rotation = data.rotation || 0;
    this._lonLatBounds = SnLonLatBounds.convert(data.lonLatBounds);
    this._id = data.id;
    this._clickable = data.clickable ?? false;
    switch (this.defaultMapSource) {
      case 'esri':
        this.overlay = this._handleEsriLayer(this._url);
        break;
      case 'google':
        this.overlay = new GoogleMapImageOverlay({
          url: this._url,
          rotation: this._rotation,
          lonLatBounds: this._lonLatBounds,
          clickable: this._clickable,
        }).googleMapImageOverlay;
        break;
    }
  }

  // TODO: Add roomsCleared() and removeRoomsCleared() functionality
  // https://taserintl.atlassian.net/browse/FWX-540

  private _handleEsriLayer(url: string): MediaLayer {
    const extent = new Extent({
      xmin: this._lonLatBounds.getWest(),
      ymin: this._lonLatBounds.getSouth(),
      xmax: this._lonLatBounds.getEast(),
      ymax: this._lonLatBounds.getNorth(),
      spatialReference: { wkid: 4326 },
    });

    const image = new ImageElement({
      image: url,
      georeference: new ExtentAndRotationGeoreference({
        extent,
        rotation: this._rotation,
      }),
    });
    const mediaLayer = new MediaLayer({
      source: [image],
      blendMode: 'normal',
      id: this._id,
    });

    mediaLayer.on('layerview-create', (e) => {
      if (this._clickable) {
        e.view.on('click', (event) => {
          (e.view as MapView).hitTest(event).then((response: any) => {
            if (response.results.length) {
              const graphic = response.results.filter((res) => {
                return res.layer?.type === 'media';
              })[0]?.element;
              if (graphic) {
                this._onClick(graphic);
              }
            }
          });
        });
      }

      e.view.on('pointer-move', (event) => {
        const screenPoint = {
          x: event.x,
          y: event.y,
        };
        (e.view as MapView).hitTest(screenPoint).then((response: any) => {
          if (response.results.length) {
            const graphic = response.results.filter((res) => {
              return res.layer?.type === 'media';
            })[0]?.element;
            if (graphic) {
              this._onHover(graphic);
            } else {
              this._onHover(null);
            }
          }
        });
      });
    });

    return mediaLayer;
  }

  private _onClick(object: any): void {
    this._newClick$.next(object);
  }

  private _onHover(object: any): void {
    this._newHover$.next(object);
  }
}
